import type { FC } from 'react';
import { useState, useId, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import { BatchEditTitleOperation } from '../../../types/batchActions';
import { BatchActionProps } from '../types';
import { validator as titleValidator } from '@wix/da-submit-shared/pkg/fields/TitleField/validation';

import s from './TitleBatchEdit.scss';

export interface Props extends BatchActionProps {
  batchEditTitle: (operation: BatchEditTitleOperation, title: string) => void;
}

const TitleBatchEdit: FC<Props> = ({ selectionArea, batchEditTitle }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [operation, setOperation] = useState<BatchEditTitleOperation>('prepend');
  const [title, setTitle] = useState('');
  const [validationResult, setValidationResult] = useState<Error>();
  const operationInputId = useId();
  const titleInputId = useId();

  const handleSave = useCallback(() => {
    batchEditTitle(operation, title);
  }, [batchEditTitle, operation, title]);

  const disableSave = useMemo(() => title.trim().length < 1, [title]);

  // Make a fake validator, this is so we can reuse the main forms
  // validation without all the form stuff coming with it
  const validator = titleValidator({ initResponse: {} as any, params: { deviationId: 0 } }, t);
  const onChange = useCallback(
    ev => {
      setTitle(ev.target.value);
      validator
        .validate({ title: ev.target.value })
        .then(() => setValidationResult(undefined))
        .catch(e => setValidationResult(e));
    },
    [validator]
  );

  const form = (
    <div className={s['form']}>
      <SelectField
        className={s['operation-field']}
        id={operationInputId}
        items={[
          {
            value: 'prepend',
            label: t('studio.batchActions.form.edit_title.operations.prepend'),
          },
          {
            value: 'append',
            label: t('studio.batchActions.form.edit_title.operations.append'),
          },
          {
            value: 'replace',
            label: t('studio.batchActions.form.edit_title.operations.replace'),
          },
        ]}
        onSelect={value => setOperation(value as BatchEditTitleOperation)}
        value={operation}
        label={t('studio.batch.operation.label')}
        isLabelHidden
        withBlankOption={false}
      />
      <TextInput
        wrapperClassName={s['title-field']}
        id={titleInputId}
        value={title}
        onChange={onChange}
        placeholder={t('studio.batchActions.form.edit_title.title.placeholder')}
        label={t('studio.batch.title.label')}
        isLabelHidden
        maxLength={50}
        validationResult={validationResult ? 'error' : undefined}
        details={validationResult && validationResult.message}
      />
    </div>
  );

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        heading={t('studio.batchActions.subtitle.edit_title')}
        onSave={handleSave}
        disableSave={disableSave}
        restrictSave="non_core_multiple"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }

  return (
    <DesktopBatchActionWrapper
      heading={t('studio.batchActions.subtitle.edit_title')}
      selectionArea={selectionArea}
      onSave={handleSave}
      disableSave={disableSave}
      restrictSave="non_core_multiple"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
TitleBatchEdit.displayName = 'TitleBatchEdit';

export default TitleBatchEdit;
